import { CSSProperties, FC, KeyboardEvent, useCallback, useMemo } from 'react'
import { ApolloError } from '@apollo/client'
import { TFunction } from 'i18next'
import { useTranslation } from 'react-i18next'
import { NewLoadingAndErrors } from '../../../components/custom_components/LoadingAndErrors/NewLoadingAndErrors'
import PerButtonCircle from '../../../components/custom_components/ButtonCircle'
import PerFlexRow from '../../../components/custom_components/FlexRow'
import PerIcon from '../../../components/custom_components/Icon'
import PerText from '../../../components/custom_components/Text'
import { useDashboardStore } from '../DashboardStore'
import { MonitoringTabs } from '../dasboardStoreTypes'
import {
  appraisalMeetingStatusToDashboardIcon,
  appraisalMonitoringStatusIconColor,
  AppraisalMonitoringTableStatusFilter,
  MONITORING_TABLE_STATUS_OPTIONS,
  hasAppraisalMonitoringFilterAreaValues,
  isMonitoringChartInvitesStatus,
  CompanyManagerAppraisalMeetingStatusCounters,
  visibleMonitoringStatusOptions,
} from '../utils/appraisalMeetingStatusDashboardUtil'
import {
  getMonitoringHistogramBarLayout,
  MonitoringHistogramBar,
  type MonitoringHistogramBarColumn,
} from './DashboardAppraisalMonitoringHistogramBar'
import DashboardAppraisalMonitoringActiveFilters from './DashboardAppraisalMonitoringActiveFilters'

type DashboardAppraisalMonitoringChartTabBodyProps = {
  countersLoading: boolean
  countersError?: ApolloError
  counters?: CompanyManagerAppraisalMeetingStatusCounters[]
  unfilteredCounters?: CompanyManagerAppraisalMeetingStatusCounters[]
}

/** Single gap between every pair of adjacent columns (all bars / icons / labels). */
const HISTOGRAM_COLUMN_GAP_PX = 6
const HISTOGRAM_ROW_GAP_PX = 8
/** Vertical inset inside the SCHEDULED gray panel (top = bottom), aligned with histogram row gap. */
const INVITES_PANEL_V_PADDING_PX = HISTOGRAM_ROW_GAP_PX

const buildStatusCounts = (counters: CompanyManagerAppraisalMeetingStatusCounters[]): Map<AppraisalMonitoringTableStatusFilter, number> => {
  const map = new Map<AppraisalMonitoringTableStatusFilter, number>()
  for (const key of MONITORING_TABLE_STATUS_OPTIONS) {
    map.set(key, 0)
  }
  for (const row of counters) {
    map.set(row.status, (map.get(row.status) ?? 0) + row.count)
  }
  return map
}

const useStatusActivation = (
  setAppraisalMonitoringTableStatus: (s?: AppraisalMonitoringTableStatusFilter) => void,
  setActiveMonitoringTab: (t: MonitoringTabs) => void,
) => {
  return useCallback(
    (status: AppraisalMonitoringTableStatusFilter) => {
      setAppraisalMonitoringTableStatus(status)
      setActiveMonitoringTab(MonitoringTabs.TABLE)
    },
    [setAppraisalMonitoringTableStatus, setActiveMonitoringTab],
  )
}

const useHistogramSeries = (
  counters: CompanyManagerAppraisalMeetingStatusCounters[] | undefined,
  unfilteredCounters: CompanyManagerAppraisalMeetingStatusCounters[] | undefined,
) => {
  return useMemo(() => {
    if (!counters || !unfilteredCounters) {
      return []
    }
    const statusCounts = buildStatusCounts(counters)
    const statuses = visibleMonitoringStatusOptions(buildStatusCounts(unfilteredCounters))
    const maxCount = Math.max(1, ...statuses.map((k) => statusCounts.get(k) ?? 0))
    return statuses.map((status) => {
      const count = statusCounts.get(status) ?? 0
      const { barWidthPx, barHeightPx } = getMonitoringHistogramBarLayout(count, maxCount)
      return { status, count, barWidthPx, barHeightPx }
    })
  }, [counters, unfilteredCounters])
}

type HistogramIconCellProps = {
  col: MonitoringHistogramBarColumn
}

const HistogramIconCell: FC<HistogramIconCellProps> = ({ col }) => {
  const iconName = appraisalMeetingStatusToDashboardIcon(col.status)
  return (
    <div className="dashboard-appraisal-monitoring-histogram-column__icon">
      <PerIcon name={iconName} size={22} color={appraisalMonitoringStatusIconColor(col.status)} variant="decoration" />
    </div>
  )
}

type MonitoringHistogramColumnProps = {
  col: MonitoringHistogramBarColumn
  onActivate: (status: AppraisalMonitoringTableStatusFilter) => void
  style?: CSSProperties
}

const MonitoringHistogramColumn: FC<MonitoringHistogramColumnProps> = ({ col, onActivate, style }) => {
  const onKeyDown = useCallback(
    (e: KeyboardEvent<HTMLDivElement>) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault()
        onActivate(col.status)
      }
    },
    [col.status, onActivate],
  )

  return (
    <div
      className="dashboard-appraisal-monitoring-histogram-column"
      role="button"
      tabIndex={0}
      style={style}
      onClick={() => onActivate(col.status)}
      onKeyDown={onKeyDown}
    >
      <MonitoringHistogramBar col={col} />
      <HistogramIconCell col={col} />
    </div>
  )
}

type HistogramLabelCellProps = {
  col: MonitoringHistogramBarColumn
  t: TFunction
}

const HistogramLabelCell: FC<HistogramLabelCellProps> = ({ col, t }) => {
  const chartStatus = col.status
  const labelKey = `dashboard.companyManager.monitoring.chart.status.${chartStatus}`
  return (
    <div
      role="button"
      tabIndex={-1}
      style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'flex-start',
        width: '90%',
      }}
    >
      <PerText fontSize="xs" align="center" fontWeight="bold">
        {t(labelKey)}
      </PerText>
    </div>
  )
}

type MonitoringHistogramGridProps = {
  series: MonitoringHistogramBarColumn[]
  invitesTitle: string
  t: TFunction
  onActivate: (status: AppraisalMonitoringTableStatusFilter) => void
}

const MonitoringHistogramGrid: FC<MonitoringHistogramGridProps> = ({ series, invitesTitle, t, onActivate }) => {
  const columnCount = series.length
  const invitesColumnIndexes = series
    .map((col, index) => (isMonitoringChartInvitesStatus(col.status) ? index + 1 : null))
    .filter((index): index is number => index != null)
  const invitesGridColumn =
    invitesColumnIndexes.length > 0
      ? `${invitesColumnIndexes[0]} / ${invitesColumnIndexes[invitesColumnIndexes.length - 1] + 1}`
      : undefined

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: `repeat(${columnCount}, minmax(0, 1fr))`,
        columnGap: HISTOGRAM_COLUMN_GAP_PX,
        rowGap: HISTOGRAM_ROW_GAP_PX,
        width: '90%',
        justifySelf: 'center',
      }}
    >
      {!!invitesGridColumn && (
        <>
          <div
            aria-hidden
            style={{
              gridColumn: invitesGridColumn,
              gridRow: '1 / 5',
              zIndex: 0,
              backgroundColor: '#f0f0f0',
              borderRadius: 12,
              minHeight: 0,
            }}
          />
          <div
            style={{
              gridColumn: invitesGridColumn,
              gridRow: 1,
              zIndex: 1,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              paddingTop: INVITES_PANEL_V_PADDING_PX,
            }}
          >
            <PerText fontWeight="bold" fontSize="s" color="default">
              {invitesTitle}
            </PerText>
          </div>
        </>
      )}
      {series.map((col, i) => (
        <MonitoringHistogramColumn
          key={String(col.status)}
          col={col}
          onActivate={onActivate}
          style={{
            gridColumn: i + 1,
            gridRow: '2 / 4',
            position: 'relative',
            zIndex: 1,
          }}
        />
      ))}
      {series.map((col, i) => {
        const isInvitesHistogramColumn = isMonitoringChartInvitesStatus(col.status)
        return (
          <div
            key={`label-${String(col.status)}`}
            className="dashboard-appraisal-monitoring-histogram-column__label"
            style={{
              gridColumn: i + 1,
              gridRow: 4,
              position: 'relative',
              zIndex: 1,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'flex-start',
              minWidth: 0,
              paddingBottom: isInvitesHistogramColumn ? INVITES_PANEL_V_PADDING_PX : undefined,
            }}
          >
            <HistogramLabelCell col={col} t={t} />
          </div>
        )
      })}
    </div>
  )
}

const DashboardAppraisalMonitoringChartTabBody: FC<DashboardAppraisalMonitoringChartTabBodyProps> = ({
  countersLoading,
  countersError,
  counters,
  unfilteredCounters,
}) => {
  const { t } = useTranslation()
  const [appraisalMonitoringFilters, setAppraisalMonitoringTableStatus, setActiveMonitoringTab, clearAppraisalMonitoringFilters] =
    useDashboardStore((state) => [
      state.appraisalMonitoringFilters,
      state.setAppraisalMonitoringTableStatus,
      state.setActiveMonitoringTab,
      state.clearAppraisalMonitoringFilters,
    ])

  const hasAppraisalMonitoringFilterAreaApplied = useMemo(
    () => hasAppraisalMonitoringFilterAreaValues(appraisalMonitoringFilters),
    [appraisalMonitoringFilters],
  )

  const onStatusActivate = useStatusActivation(setAppraisalMonitoringTableStatus, setActiveMonitoringTab)
  const barSeries = useHistogramSeries(counters, unfilteredCounters)

  const invitesTitle = t('dashboard.companyManager.monitoring.chart.invitesGroupTitle')
  const showChartContent = !countersLoading && !countersError && !!counters

  return (
    <div className="dashboard-appraisal-monitoring__tab-body dashboard-appraisal-monitoring__chart">
      <DashboardAppraisalMonitoringActiveFilters />
      <PerFlexRow align="middle" justify="start" mb="m">
        <PerFlexRow align="middle">
          <PerText fontSize="xs" color="secondary" mr="xs">
            {t('dashboard.companyManager.monitoring.table.clearFilters')}
          </PerText>
          <PerButtonCircle
            type="button"
            size="small"
            icon="delete_fill"
            tooltip={t('dashboard.companyManager.monitoring.table.clearFilters')}
            variant="pastel"
            disabled={!hasAppraisalMonitoringFilterAreaApplied}
            onClick={() => clearAppraisalMonitoringFilters()}
          />
        </PerFlexRow>
      </PerFlexRow>
      <div
        className="dashboard-appraisal-monitoring__histogram-region"
        role="img"
        aria-label={t('dashboard.companyManager.monitoring.chart.histogramAria')}
      >
        <PerFlexRow
          align="middle"
          width="fullWidth"
          direction="column"
          justify="center"
          className="dashboard-appraisal-monitoring__histogram-region-inner"
        >
          {!showChartContent ? (
            <NewLoadingAndErrors t={t} isLoading={countersLoading} hasNoData={!counters} error={countersError} showSpinner />
          ) : (
            <MonitoringHistogramGrid series={barSeries} invitesTitle={invitesTitle} t={t} onActivate={onStatusActivate} />
          )}
        </PerFlexRow>
      </div>
    </div>
  )
}

export default DashboardAppraisalMonitoringChartTabBody
